/**
 * Who may talk to the Messenger, and what it may hand on to the host.
 *
 * The JS SDK writes this app into an about:blank frame, so the frame runs in
 * the customer site's origin and the SDK trusts every message it relays. Any
 * other frame on the page (an ad, an embed, a site that opened the page in a
 * popup) can post to this window too, so every inbound message is checked
 * against its sender before anything acts on it.
 */

/**
 * The host: the window that embedded us (the JS SDK page, the native shell's
 * appnew.html, the dashboard preview) or this window itself (native shells and
 * the mock harness that post into their own window). Never a sibling frame,
 * a child frame or an opener.
 */
export const isFromHost = (
  source: MessageEventSource | null | undefined,
  win: Window = window,
): boolean => !!source && (source === win.parent || source === win);

/**
 * `type: "app"` messages come from the app frame the Messenger itself
 * created (AppView), and only while it still shows the app's own origin.
 */
export const isFromAppFrame = (
  event: Pick<MessageEvent, "source" | "origin">,
  appFrame?: HTMLIFrameElement | null,
  appUrl?: string | null,
): boolean => {
  const frameWindow = appFrame?.contentWindow;
  if (!frameWindow || !event.source || event.source !== frameWindow) {
    return false;
  }
  if (!appUrl) {
    return true;
  }
  try {
    return event.origin === new URL(appUrl).origin;
  } catch (exp) {
    return false;
  }
};

// Schemes that run script or load arbitrary content in whatever window
// navigates to them. The host page navigates to what the Messenger asks for
// (the SDK's url handler), so these are never handed on.
const BLOCKED_SCHEMES = new Set([
  "javascript:",
  "vbscript:",
  "data:",
  "blob:",
  "file:",
  "filesystem:",
  "about:",
]);

/**
 * Whether a link may be handed to the host to open. Web links, mailto/tel,
 * relative links and app deep links (`myapp://…`, opened by the native SDKs
 * or a custom `Gleap.setUrlHandler`) pass; script-capable schemes don't.
 * Parsed the way the browser will (it strips tabs/newlines and leading
 * spaces, so `java\tscript:` is caught too).
 */
export const isOpenableUrl = (url: unknown): url is string => {
  if (typeof url !== "string" || !url.trim()) {
    return false;
  }
  let parsed: URL;
  try {
    // Relative links resolve against a dummy https base, i.e. are allowed.
    parsed = new URL(url, "https://relative.invalid/");
  } catch (exp) {
    return false;
  }
  return !BLOCKED_SCHEMES.has(parsed.protocol.toLowerCase());
};

/**
 * Session fields a configured link may carry (`{{session.email}}` …).
 * Credentials (gleapHash, gleapId, fileAccessToken, …) are never filled in:
 * the host opens the URL, so whatever is filled in leaves the widget.
 */
const URL_PLACEHOLDER_FIELDS = new Set([
  "id",
  "userId",
  "email",
  "name",
  "phone",
  "companyId",
  "companyName",
  "plan",
  "value",
  "lang",
]);

/**
 * Replaces `{{session.<field>}}` for the harmless fields above; every other
 * placeholder becomes empty. Values are URL-encoded.
 */
export const fillSessionPlaceholders = (url: string, session: any): string =>
  url.replace(/{{\s*session\.(\w+)\s*}}/g, (_match, field: string) => {
    if (!URL_PLACEHOLDER_FIELDS.has(field)) {
      return "";
    }
    const value = session?.[field];
    if (value === undefined || value === null || typeof value === "object") {
      return "";
    }
    return encodeURIComponent(String(value));
  });

/**
 * `session-update` hosts: http(s) API URL, plain hostname[:port] realtime
 * host. Anything else is ignored.
 */
export const isValidApiUrl = (apiUrl: unknown): apiUrl is string => {
  if (typeof apiUrl !== "string" || !apiUrl) {
    return false;
  }
  try {
    const parsed = new URL(apiUrl);
    return (
      (parsed.protocol === "https:" || parsed.protocol === "http:") &&
      !parsed.username &&
      !parsed.password
    );
  } catch (exp) {
    return false;
  }
};

export const isValidRealtimeHost = (host: unknown): host is string =>
  typeof host === "string" &&
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*(:\d{1,5})?$/i.test(
    host.trim(),
  );

/**
 * Only an http(s) origin can be pinned. A file:// page (a local HTML file, an
 * Electron shell) or an app-scheme page (capacitor://, app://) has an opaque
 * origin: postMessage with that targetOrigin never matches it, so the message
 * is dropped without an error and the host never hears from the Messenger.
 */
const pinnableOrigin = (origin: string | null | undefined): string | null =>
  origin && /^https?:\/\//i.test(origin) ? origin : null;

/**
 * Where to post messages for the host. When the parent shares our origin
 * (the SDK's about:blank frame, the native shell page) the message is pinned
 * to it; otherwise the parent's origin as the browser reports it; '*' when
 * neither is known or the host's origin cannot be pinned (see above).
 */
export const hostTargetOrigin = (target: Window, win: Window = window): string => {
  try {
    // window.origin is the document's real origin (also for an about:blank
    // document); location.origin is the fallback for older browsers.
    const origin = pinnableOrigin((target as any).origin || target.location.origin);
    if (origin) {
      return origin;
    }
  } catch (exp) {
    // Cross-origin parent (legacy src-loaded frame, dashboard preview).
  }
  try {
    const ancestor = pinnableOrigin(win.location.ancestorOrigins?.[0]);
    if (ancestor) {
      return ancestor;
    }
  } catch (exp) {}
  return "*";
};
